import React, {useState, useEffect } from 'react';
import authService from '../../api-authorization/AuthorizeService'
import axios from 'axios'
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import createCache from '@emotion/cache';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const muiCache = createCache({
    key: 'mui-datatables',
    prepend: true,
});

export default function PlayerAccountInfo({idType, id }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [playerAccountInfo, setPlayerAccountInfo] = useState();
    const apiPath = '/api/NACHUsers/GetPlayerAccountByID?id=' + id + '&idType=' + idType;
    const [finishedLoading, setFinishedLoading] = useState(false);

    useEffect(async () => {
        var token = await authService.getAccessToken();
        var headers = {
            headers: { 'Authorization': `Bearer ${token}` }
        }
        axios.get(apiPath, headers).then((response) => {
            setPlayerAccountInfo(response.data);
            setFinishedLoading(true);
        }).catch((error) => {
            console.log(JSON.stringify(error));
        });
    }, []); //Runs only on the first render

    // Mobile-friendly info row component
    const InfoRow = ({ label, value }) => (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            py: 0.5,
            borderBottom: '1px solid #eee'
        }}>
            <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                    minWidth: isMobile ? 'auto' : '120px',
                    fontWeight: 500,
                    fontSize: isMobile ? '11px' : '12px'
                }}
            >
                {label}
            </Typography>
            <Typography 
                variant="body2" 
                sx={{ 
                    fontSize: isMobile ? '12px' : '14px',
                    wordBreak: 'break-word'
                }}
            >
                {value}
            </Typography>
        </Box>
    );

    return (
        <div className="mt-10 mb-10">
        {playerAccountInfo &&
            <Box sx={{ pt: 1, pb: 1, px: isMobile ? 1 : 0 }}>
                <Grid container direction="row" rowSpacing={1} columnSpacing={1} alignItems="flex-start">
                    <Grid item xs={12}>
                        <Card>
                            <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                                <Typography 
                                    gutterBottom 
                                    variant={isMobile ? "subtitle1" : "h5"} 
                                    component="div" 
                                    color="text.primary"
                                    sx={{ fontWeight: 500, mb: isMobile ? 1 : 2 }}
                                >
                                    NACH Account: {idType} {id} {playerAccountInfo.name}
                                </Typography>
                                
                                {isMobile ? (
                                    // Mobile layout - single column
                                    <Box>
                                        <InfoRow label="ID" value={playerAccountInfo.id} />
                                        <InfoRow label="User Name" value={playerAccountInfo.userName} />
                                        <InfoRow label="Membership" value={playerAccountInfo.membershipLevel} />
                                        <InfoRow label="NACH Handle" value={playerAccountInfo.nachHandle} />
                                        <InfoRow label="FIDE ID" value={playerAccountInfo.userFideId} />
                                        <InfoRow label="USCF ID" value={playerAccountInfo.userUscfId} />
                                        <InfoRow label="Created On" value={playerAccountInfo.createdOn} />
                                        <InfoRow label="Last Log In" value={playerAccountInfo.lastLogIn} />
                                        <InfoRow label="Created From" value={playerAccountInfo.createdFromIP} />
                                        <InfoRow label="Last Log In From" value={playerAccountInfo.lastLogInFromIP} />
                                        <InfoRow 
                                            label="Email" 
                                            value={`${playerAccountInfo.email} ${playerAccountInfo.emailConfirmed ? "[C]" : "[N]"}`} 
                                        />
                                        <InfoRow label="Phone" value={playerAccountInfo.phoneNumber || '-'} />
                                        <InfoRow 
                                            label="Supporter Exp" 
                                            value={playerAccountInfo.isSupporter ? `[Y] ${playerAccountInfo.supporterStatusExpDateUtc}` : "[N]"} 
                                        />
                                        <InfoRow 
                                            label="PayPal Trans" 
                                            value={playerAccountInfo.supporterDonationPayPalTransactionId ? (
                                                <a href={"https://www.paypal.com/activity/payment/" + playerAccountInfo.supporterDonationPayPalTransactionId} target="_blank" rel="noreferrer">
                                                    {playerAccountInfo.supporterDonationPayPalTransactionId}
                                                </a>
                                            ) : '-'} 
                                        />
                                    </Box>
                                ) : (
                                    // Desktop layout - two column table
                                    <table>
                                        <tbody>
                                            <tr>
                                                <td align="right" nowrap="nowrap">ID</td>
                                                <td align="right" nowrap="nowrap">   :   </td>
                                                <td align="left" nowrap="nowrap">{playerAccountInfo.id}</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">|</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">User Name</td>
                                                <td align="right" nowrap="nowrap">   :   </td>
                                                <td align="left" nowrap="nowrap">{playerAccountInfo.userName}</td>
                                            </tr>
                                            <tr>
                                                <td align="right" nowrap="nowrap">Membership</td>
                                                <td align="right" nowrap="nowrap">   :   </td>
                                                <td align="left" nowrap="nowrap">{playerAccountInfo.membershipLevel}</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">|</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">NACH Handle</td>
                                                <td align="right" nowrap="nowrap">   :   </td>
                                                <td align="left" nowrap="nowrap">{playerAccountInfo.nachHandle}</td>
                                            </tr>
                                            <tr>
                                                <td align="right" nowrap="nowrap">FIDE ID</td>
                                                <td align="right" nowrap="nowrap">   :   </td>
                                                <td align="left" nowrap="nowrap">{playerAccountInfo.userFideId}</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">|</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">USCF ID</td>
                                                <td align="right" nowrap="nowrap">   :   </td>
                                                <td align="left" nowrap="nowrap">{playerAccountInfo.userUscfId}</td>
                                            </tr>
                                            <tr>
                                                <td align="right" nowrap="nowrap">Created On</td>
                                                <td align="right" nowrap="nowrap">   :   </td>
                                                    <td align="left" nowrap="nowrap">{playerAccountInfo.createdOn}</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">|</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">Last Log In</td>
                                                <td align="right" nowrap="nowrap">   :   </td>
                                                <td align="left" nowrap="nowrap">{playerAccountInfo.lastLogIn}</td>
                                            </tr>
                                            <tr>
                                                <td align="right" nowrap="nowrap">Created From</td>
                                                <td align="right" nowrap="nowrap">   :   </td>
                                                <td align="left" nowrap="nowrap">{playerAccountInfo.createdFromIP}</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">|</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">Last Log In From</td>
                                                <td align="right" nowrap="nowrap">   :   </td>
                                                <td align="left" nowrap="nowrap">{playerAccountInfo.lastLogInFromIP}</td>
                                            </tr>
                                            <tr>
                                                <td align="right" nowrap="nowrap">Email</td>
                                                <td align="right" nowrap="nowrap">   :   </td>
                                                    <td align="left" nowrap="nowrap">{playerAccountInfo.email} {playerAccountInfo.emailConfirmed ?"[C]":"[N]"}</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">|</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">Phone</td>
                                                <td align="right" nowrap="nowrap">   :   </td>
                                                <td align="left" nowrap="nowrap">{playerAccountInfo.phoneNumber}</td>
                                            </tr>
                                            <tr>
                                                <td align="right" nowrap="nowrap">Supporter Exp</td>
                                                    <td align="right" nowrap="nowrap">   :   </td>
                                                    <td align="left" nowrap="nowrap">{playerAccountInfo.isSupporter ? "[Y]" + playerAccountInfo.supporterStatusExpDateUtc : "[N]"}</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">|</td>
                                                <td align="right" nowrap="nowrap">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
                                                <td align="right" nowrap="nowrap">PayPal Trans </td>
                                                    <td align="right" nowrap="nowrap">   :   </td>
                                                    <td align="left" nowrap="nowrap"><a href={"https://www.paypal.com/activity/payment/"+playerAccountInfo.supporterDonationPayPalTransactionId} target="_blank">{playerAccountInfo.supporterDonationPayPalTransactionId}</a></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        }
        {!playerAccountInfo &&
            <Box sx={{ pt: 1, pb: 1, px: isMobile ? 1 : 0 }}>
                <Grid container direction="row" rowSpacing={1} columnSpacing={1} alignItems="flex-start">
                    <Grid item xs={12}>
                        <Card>
                            <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                                <Typography 
                                    gutterBottom 
                                    variant={isMobile ? "subtitle1" : "h5"} 
                                    component="div" 
                                    color="text.primary"
                                >
                                    No NACH Account Found: {idType} ID {id}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        }
        </div>
    )
}


//{
//    "fidePlayerId": 2088215, "uscfPlayerId": 14729222, "fideName": "He, Anthony", "fideCountry": "USA", "fideSex": "M", "fideTitle": "IM",
//        "fideWomenTitle": "", "fideOtherTitle": "", "fidefoaTitle": "", "fideRating": 2501, "fideGames": 0, "fidekValue": 10, "fideRapidRating": 0,
//        "fideRapidGames": 0, "fideRapidKValue": 0, "fideBlitzRating": 2409, "fideBlitzGames": 0, "fideBlitzKValue": 10, "fideBirthday": "2005",
//        "fideFlag": "", "fideRatingListDate": "2023-04-01T00:00:00", "uscfName": "He, Anthony Bi", "uscfState": "WA", "uscfCountry": "USA",
//        "uscfExpDate": "2023-12-31T00:00:00", "uscfStatus": "A", "uscfFideIso": "USA", "uscfRtgListDate": "2023-04-01T00:00:00", "uscfRating": 2489,
//        "uscfGames": 3, "uscfQuickRating": 2097, "uscfQuickGames": 0, "uscfBlitzRating": 2517, "uscfBlitzGames": 0, "uscfOnlineRating": 2550,
//        "uscfOnlineGames": 0, "uscfOnlineQuickRating": 0, "uscfOnlineQuickGames": 0, "uscfOnlineBlitzRating": 2549, "uscfOnlineBlitzGames": 0,
//            "uscfMembershipExpired": false, "chessComID": "0gzpanda", "liChessID": "adnaPZg0", "chess24ID": null, "twitchID": null, "email": "", "phone": ""
//}